import React, { useCallback } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import * as analytics from "@/services/analytics";
import { useLocation, useNavigate, useSearchParams } from "react-router";

interface FormInputs {
  query: string;
}

interface DriveSearchProps {
  folderId?: string;
}

export const DriveSearch: React.FC<DriveSearchProps> = ({ folderId }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const methods = useForm<FormInputs>({
    defaultValues: {
      query: searchParams.get("q") || "",
    },
  });

  const searchQuery = methods.watch("query");

  const updateUrl = useCallback(
    (query: string) => {
      query = query.trim();
      const newSearchParams = new URLSearchParams(searchParams);
      if (query) {
        newSearchParams.set("q", query);
      } else {
        newSearchParams.delete("q");
      }
      const searchQueryString = newSearchParams.toString();
      navigate(pathname + (searchQueryString ? "?" + searchQueryString : ""));

      analytics.searchFilesAndFolders(query, folderId);

      methods.setValue("query", query);
    },
    [navigate, pathname, searchParams, methods, folderId],
  );

  const onSubmit = (payload: FormInputs) => {
    if (payload.query) {
      updateUrl(payload.query.trim());
    }
  };

  const handleSearch = () => {
    analytics.search(methods.getValues("query") || "");
  };

  const handleClear = () => {
    updateUrl("");
  };

  return (
    <form method="post" onSubmit={methods.handleSubmit(onSubmit)} className="mb-4 flex items-center">
      <div className="relative flex-grow">
        <input
          type="text"
          {...methods.register("query")}
          placeholder="Search files and folders"
          className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none"
        />
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
      </div>
      <button
        onClick={handleSearch}
        type="submit"
        className="ml-2 rounded-md bg-desert-sand-700 px-4 py-2 text-white hover:bg-desert-sand-900 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-opacity-50"
      >
        Search
      </button>
      {searchQuery && (
        <button
          onClick={handleClear}
          className="ml-2 rounded-md bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
        >
          Clear
        </button>
      )}
    </form>
  );
};
